/**
 * Statistiche globali di consumo LLM (F-065).
 *
 * `callsWithoutUsage` è mostrato accanto a ogni totale e non nascosto: sono le
 * chiamate per cui il provider non ha restituito i conteggi. Un totale che le
 * ignora sembra completo e non lo è, ed è esattamente il modo in cui una misura
 * diventa fuorviante proprio mentre sembra precisa.
 */

import { useState, type ReactNode } from 'react';
import { ApiError } from '@/lib/api-client';
import { formatDate, formatNumber } from '@/lib/format';
import { Alert, Card, Select, Spinner, StatCard, TableWrap } from '@/ui';
import { useLlmStats, type UsageBucket } from './api';

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) return error.message;
  return error instanceof Error ? error.message : fallback;
}

/**
 * Il costo in dollari, con **quattro decimali**: una singola chiamata costa
 * frazioni di centesimo e due decimali la mostrerebbero come «$0,00», cioe'
 * gratis. Formattato dalla stringa decimale, mai da un `number` calcolato.
 */
function formatCost(value: string | null): string {
  if (value === null) return '—';
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '—';
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  }).format(numeric);
}

/**
 * La provenienza del costo, accanto al costo (SPEC-023 §4).
 *
 * ⚠️ Non e' pignoleria: `costEstimated` sono righe prezzate con lo specchio del
 * catalogo di **oggi**, cioe' con un prezzo che al momento della spesa poteva
 * essere un altro, e `costUnknown` sono righe che nel totale **non ci sono**.
 * Un costo senza queste due cifre accanto si legge come esatto.
 */
function CostNote({ estimated, unknown }: { estimated: number; unknown: number }) {
  if (estimated === 0 && unknown === 0) return null;
  const parts = [
    estimated > 0 ? `${formatNumber(estimated)} stimate` : null,
    unknown > 0 ? `${formatNumber(unknown)} non misurate` : null,
  ].filter(Boolean);
  return <div className="admin__muted">{parts.join(' · ')}</div>;
}

const WINDOWS = [
  { value: '7', label: 'Ultimi 7 giorni' },
  { value: '30', label: 'Ultimi 30 giorni' },
  { value: '90', label: 'Ultimi 90 giorni' },
  { value: '365', label: 'Ultimo anno' },
];

function BucketTable<T extends UsageBucket>({
  buckets,
  header,
  labelOf,
}: {
  buckets: T[];
  header: string;
  labelOf?: (bucket: T) => ReactNode;
}) {
  return (
    <TableWrap>
      <table className="table table--compact">
        <thead>
          <tr>
            <th>{header}</th>
            <th className="numeric">Chiamate</th>
            <th className="numeric">Input</th>
            <th className="numeric">Output</th>
            <th className="numeric">Totale token</th>
            <th className="numeric">Costo</th>
            <th className="numeric">Senza conteggi</th>
          </tr>
        </thead>
        <tbody>
          {buckets.map((bucket) => (
            <tr key={bucket.key}>
              <td>{labelOf ? labelOf(bucket) : <code>{bucket.key}</code>}</td>
              <td className="numeric">{formatNumber(bucket.calls)}</td>
              <td className="numeric">{formatNumber(bucket.promptTokens)}</td>
              <td className="numeric">{formatNumber(bucket.completionTokens)}</td>
              <td className="numeric">{formatNumber(bucket.totalTokens)}</td>
              <td className="numeric">
                {formatCost(bucket.cost)}
                <CostNote estimated={bucket.costEstimated} unknown={bucket.costUnknown} />
              </td>
              <td className="numeric">{bucket.callsWithoutUsage > 0 ? formatNumber(bucket.callsWithoutUsage) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableWrap>
  );
}

export function AdminLlmUsagePage() {
  const [days, setDays] = useState(30);
  const stats = useLlmStats(days);

  return (
    <>
      <Card title="Periodo">
        <Select
          label="Finestra"
          value={String(days)}
          onChange={(event) => setDays(Number(event.target.value))}
          options={WINDOWS}
          hint="I totali qui sotto valgono solo per questo periodo."
        />
      </Card>

      {stats.isPending ? (
        <Spinner />
      ) : stats.error ? (
        <Alert variant="error" title="Statistiche non caricate">
          {errorMessage(stats.error, 'Errore imprevisto')}
        </Alert>
      ) : stats.data.totals.calls === 0 ? (
        <Card>
          <p>Nessuna chiamata LLM registrata nel periodo selezionato.</p>
        </Card>
      ) : (
        <>
          <div className="admin__stats">
            <StatCard label="Chiamate" value={formatNumber(stats.data.totals.calls)} />
            <StatCard label="Token totali" value={formatNumber(stats.data.totals.totalTokens)} />
            <StatCard
              label="Token in ingresso"
              value={formatNumber(stats.data.totals.promptTokens)}
              hint={`${formatNumber(stats.data.totals.completionTokens)} in uscita`}
            />
            <StatCard
              label="Utenti attivi"
              value={formatNumber(stats.data.totals.activeUsers)}
              hint="Con almeno una chiamata nel periodo"
            />
            <StatCard
              label="Chiamate senza conteggi"
              value={formatNumber(stats.data.totals.callsWithoutUsage)}
              hint="Il provider non ha restituito usage: token reali non misurati"
              tone={stats.data.totals.callsWithoutUsage > 0 ? 'negative' : 'neutral'}
            />
            <StatCard
              label="Costo misurato"
              value={formatCost(stats.data.totals.cost)}
              hint={
                stats.data.totals.costEstimated > 0 || stats.data.totals.costUnknown > 0
                  ? `${formatNumber(stats.data.totals.costEstimated)} stimate dal catalogo di oggi, ${formatNumber(stats.data.totals.costUnknown)} non misurate`
                  : 'Prezzi fissati al momento di ogni chiamata'
              }
            />
          </div>

          {/* ⚠️ La voce piu' cara del sistema NON e' in questi totali, e dirlo e'
              parte della misura: le analisi TradingAgents vivono nel servizio
              `ai-analysis/`, pagano le proprie chiamate a OpenRouter e non
              scrivono in `llm_usage`. Si dichiara quante sono; non si stima
              quanto sono costate, perche' una stima al posto di una misura e'
              esattamente cio' che questa pagina esiste per non fare. */}
          <Alert variant="warning" title="Questi totali non sono la spesa completa">
            Nel periodo ci sono <strong>{formatNumber(stats.data.totals.tradingAgentsAnalyses)}</strong> analisi
            TradingAgents, che pagano le proprie chiamate nel servizio di analisi e non passano da questo
            registro: il loro costo è <strong>non misurato</strong>, non incluso e non stimato. È
            verosimilmente la voce più cara.
          </Alert>

          <Card title="Per operazione" subtitle="Che cosa il sistema stava facendo." flush>
            <BucketTable buckets={stats.data.byOperation} header="Operazione" />
          </Card>

          <Card
            title="Per modello"
            subtitle="Il modello EFFETTIVAMENTE servito, che può differire da quello richiesto: OpenRouter instrada."
            flush
          >
            <BucketTable buckets={stats.data.byModel} header="Modello" />
          </Card>

          <Card title="Per utente" flush>
            <BucketTable
              buckets={stats.data.byUser}
              header="Utente"
              labelOf={(row) => (
                <>
                  {row.displayName ?? row.email}
                  {row.displayName && <div className="admin__muted">{row.email}</div>}
                </>
              )}
            />
          </Card>

          <Card title="Per giorno" subtitle="Date in UTC." flush>
            <TableWrap tall>
              <table className="table table--compact table--sticky-head">
                <thead>
                  <tr>
                    <th>Giorno</th>
                    <th className="numeric">Chiamate</th>
                    <th className="numeric">Token</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.data.byDay.map((day) => (
                    <tr key={day.date}>
                      <td>{formatDate(day.date)}</td>
                      <td className="numeric">{formatNumber(day.calls)}</td>
                      <td className="numeric">{formatNumber(day.totalTokens)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableWrap>
          </Card>
        </>
      )}
    </>
  );
}
